import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  Plus, Search, Star, MapPin, Users, Ruler, Trash2, Pencil, Building2, Phone, Mail, Globe,
  DollarSign, Upload, X, FileText, ExternalLink, Check, Sparkles, Loader2, Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  VenueForm, emptyForm, formatPriceRange, FormBlock, F, Section, MetaCell, InfoRow,
} from "./venues/VenueForm";

export default function Venues() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState("directory"); // 'directory' | 'search'
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(routeId || null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState("");
  const [urlInput, setUrlInput] = useState("");

  // Google search state
  const [gQuery, setGQuery] = useState("");
  const [gResults, setGResults] = useState(null); // null = never searched, array = results
  const [gSavedIds, setGSavedIds] = useState([]);
  const [gLoading, setGLoading] = useState(false);
  const [gDetailId, setGDetailId] = useState(null); // Google place_id currently being viewed
  const [gConfigured, setGConfigured] = useState(true); // false when backend returns 503 for the search endpoint

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/venues`);
      setVenues(data || []);
    } catch { toast.error("Could not load venues"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (routeId) setDetailId(routeId); }, [routeId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return venues;
    return venues.filter(v =>
      v.name.toLowerCase().includes(query) ||
      (v.city || "").toLowerCase().includes(query) ||
      (v.region || "").toLowerCase().includes(query) ||
      (v.amenities || []).some(a => a.toLowerCase().includes(query)) ||
      (v.amenity_tags || []).some(t => t.toLowerCase().includes(query))
    );
  }, [venues, q]);

  const detailVenue = detailId ? venues.find(v => v.id === detailId) : null;

  const openCreate = () => { setForm(emptyForm); setTagInput(""); setEditing("new"); };
  const openEdit = (v) => {
    setForm({
      ...emptyForm, ...v,
      capacity_seated: v.capacity_seated ?? "",
      capacity_standing: v.capacity_standing ?? "",
      square_feet: v.square_feet ?? "",
      price_min: v.price_min ?? "",
      price_max: v.price_max ?? "",
      amenities: v.amenities || [],
      amenity_tags: v.amenity_tags || [],
    });
    setTagInput("");
    setEditing(v.id);
  };

  const submitVenue = async () => {
    if (!form.name.trim()) { toast.error("Give the venue a name"); return; }
    const payload = {
      ...form,
      name: form.name.trim(),
      capacity_seated: form.capacity_seated === "" ? null : Number(form.capacity_seated),
      capacity_standing: form.capacity_standing === "" ? null : Number(form.capacity_standing),
      square_feet: form.square_feet === "" ? null : Number(form.square_feet),
      price_min: form.price_min === "" ? null : Number(form.price_min),
      price_max: form.price_max === "" ? null : Number(form.price_max),
    };
    try {
      if (editing === "new") {
        const { data } = await api.post(`/venues`, payload);
        toast.success("Venue added to directory");
        setDetailId(data.id);
      } else {
        await api.patch(`/venues/${editing}`, payload);
        toast.success("Venue updated");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save venue"); }
  };

  const del = async (v) => {
    if (!confirm(`Delete "${v.name}"?`)) return;
    try { await api.delete(`/venues/${v.id}`); toast.success("Venue removed"); setDetailId(null); await load(); }
    catch { toast.error("Could not delete"); }
  };

  const toggleFav = async (v) => {
    try { await api.patch(`/venues/${v.id}`, { is_favorite: !v.is_favorite }); await load(); }
    catch { toast.error("Could not update"); }
  };

  const uploadFloorPlan = async (venueId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/venues/${venueId}/floor-plans`, fd); toast.success("Floor plan uploaded"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const addFloorPlanUrl = async (venueId) => {
    const url = urlInput.trim(); if (!url) return;
    try { await api.post(`/venues/${venueId}/floor-plans/url`, { url }); toast.success("Floor plan link added"); setUrlInput(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add link"); }
  };
  const removeFloorPlan = async (venueId, fpId) => {
    if (!confirm("Remove this floor plan?")) return;
    try { await api.delete(`/venues/${venueId}/floor-plans/${fpId}`); await load(); }
    catch { toast.error("Could not remove"); }
  };

  // Google search
  const runGoogle = async () => {
    const query = gQuery.trim();
    if (!query) return;
    setGLoading(true);
    try {
      const { data } = await api.get(`/venues/google/search`, { params: { q: query } });
      setGResults(data.places || []);
      setGSavedIds(data.saved_place_ids || []);
      setGConfigured(true);
    } catch (e) {
      if (e.response?.status === 503) {
        setGConfigured(false); setGResults([]);
      } else {
        toast.error(e.response?.data?.detail || "Search failed");
      }
    } finally { setGLoading(false); }
  };
  const saveGooglePlace = async (place_id) => {
    try {
      const { data } = await api.post(`/venues/from-google/${place_id}`);
      toast.success(`"${data.name}" saved to your directory`);
      setGSavedIds(ids => [...ids, place_id]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  return (
    <Shell>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">/venues · SHARED_DIRECTORY</div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-app tracking-tight leading-none">Venues</h1>
            <p className="mt-2 text-sm text-muted">
              <span className="font-mono tabular-nums">{venues.length.toString().padStart(2, "0")}</span> venue{venues.length === 1 ? "" : "s"} in your directory · reusable across every event
            </p>
          </div>
          <Button onClick={openCreate} data-testid="add-venue-btn" className="rounded-sm bg-app hover:bg-black text-white h-10 px-4">
            <Plus size={15} strokeWidth={2} className="mr-2" /> Add venue
          </Button>
        </div>

        {/* View switch */}
        <div className="border-b border-hair mb-5 flex items-center gap-6">
          {[
            ["directory", "My directory", `${venues.length.toString().padStart(2, "0")}`],
            ["search", "Search Google", <Sparkles key="s" size={11} strokeWidth={2} className="inline mr-1" />],
          ].map(([key, label, right]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              data-testid={`venue-view-${key}`}
              className={`relative py-3 px-1 text-sm font-medium ${view === key ? "text-app" : "text-muted hover:text-app"}`}
            >
              <span className="inline-flex items-center gap-1.5">
                {typeof right === "object" ? right : null}
                {label}
                {typeof right === "string" && <span className="font-mono text-[10px] tabular-nums text-faint">[{right}]</span>}
              </span>
              {view === key && <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-app" />}
            </button>
          ))}
        </div>

        {view === "directory" ? (
          <DirectoryView
            loading={loading}
            venues={filtered}
            q={q} setQ={setQ}
            onOpen={(id) => { setDetailId(id); navigate(`/venues/${id}`); }}
            onFav={toggleFav}
            onCreate={openCreate}
          />
        ) : (
          <GoogleSearchView
            gQuery={gQuery} setGQuery={setGQuery}
            gResults={gResults} gLoading={gLoading}
            gSavedIds={gSavedIds}
            gConfigured={gConfigured}
            runGoogle={runGoogle}
            onOpen={setGDetailId}
            onSave={saveGooglePlace}
          />
        )}
      </div>

      {/* Directory venue detail */}
      <Dialog open={!!detailVenue} onOpenChange={(o) => { if (!o) { setDetailId(null); if (routeId) navigate("/venues"); } }}>
        <DialogContent className="bg-surface rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailVenue && (
            <VenueDetail
              v={detailVenue}
              onEdit={() => { setDetailId(null); openEdit(detailVenue); }}
              onDelete={() => del(detailVenue)}
              onFav={() => toggleFav(detailVenue)}
              onUpload={(file) => uploadFloorPlan(detailVenue.id, file)}
              onAddUrl={() => addFloorPlanUrl(detailVenue.id)}
              onRemoveFp={(fpId) => removeFloorPlan(detailVenue.id, fpId)}
              urlInput={urlInput} setUrlInput={setUrlInput}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Google place detail */}
      <Dialog open={!!gDetailId} onOpenChange={(o) => !o && setGDetailId(null)}>
        <DialogContent className="bg-surface rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          {gDetailId && (
            <GooglePlaceDetail
              placeId={gDetailId}
              onSave={saveGooglePlace}
              onClose={() => setGDetailId(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create / edit form */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-app">{editing === "new" ? "Add a venue" : "Edit venue"}</DialogTitle>
            <DialogDescription>Anything you enter here is reusable across every event in your studio.</DialogDescription>
          </DialogHeader>
          <VenueForm form={form} setForm={setForm} tagInput={tagInput} setTagInput={setTagInput} />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitVenue} data-testid="venue-form-submit" className="rounded-sm bg-app hover:bg-black">
              {editing === "new" ? "Add to directory" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

// ============ DIRECTORY VIEW ============
function DirectoryView({ loading, venues, q, setQ, onOpen, onFav, onCreate }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 bevel border-hair rounded-sm px-3 py-2">
        <Search size={14} strokeWidth={1.5} className="text-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city, or amenity…" className="w-full bg-transparent outline-none text-sm placeholder:text-muted" data-testid="venue-search" />
      </div>
      {loading ? (
        <div className="text-muted font-mono text-xs">LOADING…</div>
      ) : venues.length === 0 ? (
        <div className="bevel border-hair p-10 text-center rounded-sm" data-testid="venues-empty">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">SYSTEM STATE / EMPTY_DIRECTORY</div>
          <div className="font-display text-xl font-bold text-app mb-2 tracking-tight">{q ? "No venues match that search." : "Your venue directory is empty."}</div>
          <p className="text-sm text-muted max-w-md mx-auto mb-5">
            {q ? "Try a broader search — or search Google for real event venues nearby." : "Add venues manually, or search Google Business Profiles to import them with photos, reviews, and contact info."}
          </p>
          <Button onClick={onCreate} className="rounded-sm bg-app hover:bg-black">
            <Plus size={14} strokeWidth={2} className="mr-2" /> Add first venue
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {venues.map(v => <DirectoryCard key={v.id} v={v} onOpen={() => onOpen(v.id)} onFav={() => onFav(v)} />)}
        </div>
      )}
    </div>
  );
}

function DirectoryCard({ v, onOpen, onFav }) {
  const firstImg = (v.floor_plans || []).find(fp => fp.kind === "image");
  return (
    <div className="group relative bevel border-hair card-lift rounded-sm" data-testid={`venue-card-${v.id}`}>
      <button type="button" onClick={onFav} data-testid={`venue-fav-${v.id}`} className="absolute top-2 right-2 p-1.5 z-10 text-muted hover:text-[#F59E0B]" title="Favorite">
        <Star size={15} strokeWidth={1.5} fill={v.is_favorite ? "#F59E0B" : "none"} className={v.is_favorite ? "text-[#F59E0B]" : ""} />
      </button>
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="aspect-[16/9] bg-surface-2 border-b border-hair flex items-center justify-center overflow-hidden">
          {firstImg ? (
            <img alt={v.name} src={`${process.env.REACT_APP_BACKEND_URL}/api/venues/${v.id}/floor-plans/${firstImg.id}/download`} className="w-full h-full object-cover" />
          ) : (
            <Building2 size={36} strokeWidth={1} className="text-faint" />
          )}
        </div>
        <div className="p-4">
          {v.google_place_id && <div className="font-mono text-[9px] tracking-widest uppercase text-muted mb-1">Google</div>}
          <div className="font-display text-lg font-bold text-app tracking-tight leading-tight">{v.name}</div>
          {(v.city || v.region) && <div className="text-xs text-muted mt-1 flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} className="text-muted" />{[v.city, v.region].filter(Boolean).join(", ")}</div>}
          <div className="mt-3 pt-3 border-t border-hair grid grid-cols-2 gap-2 text-xs">
            {(v.capacity_seated || v.capacity_standing) && <div className="flex items-center gap-1 text-app"><Users size={11} strokeWidth={1.5} className="text-muted" /><span className="font-mono tabular-nums">{v.capacity_seated || "–"}<span className="text-faint">/</span>{v.capacity_standing || "–"}</span></div>}
            {v.square_feet && <div className="flex items-center gap-1 text-app"><Ruler size={11} strokeWidth={1.5} className="text-muted" /><span className="font-mono tabular-nums">{v.square_feet.toLocaleString()} ft²</span></div>}
            {(v.price_min || v.price_max) && <div className="col-span-2 flex items-center gap-1 text-app"><DollarSign size={11} strokeWidth={1.5} className="text-muted" /><span className="font-mono tabular-nums">{formatPriceRange(v)}</span></div>}
          </div>
          {(v.amenities || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {(v.amenities || []).slice(0, 3).map(a => <span key={a} className="eyebrow px-1.5 py-0.5 bg-surface-2 text-app">{a}</span>)}
              {(v.amenities || []).length > 3 && <span className="eyebrow px-1.5 py-0.5 text-muted">+{v.amenities.length - 3}</span>}
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

// ============ GOOGLE SEARCH VIEW ============
function GoogleSearchView({ gQuery, setGQuery, gResults, gLoading, gSavedIds, gConfigured, runGoogle, onOpen, onSave }) {
  return (
    <div className="space-y-5">
      <form onSubmit={(e) => { e.preventDefault(); runGoogle(); }} className="flex items-center gap-2 bevel border-hair rounded-sm px-3 py-2">
        <Search size={14} strokeWidth={1.5} className="text-muted" />
        <input
          value={gQuery}
          onChange={(e) => setGQuery(e.target.value)}
          placeholder="e.g. wedding venue Portland OR · rooftop event space Brooklyn · barn wedding Napa"
          className="w-full bg-transparent outline-none text-sm placeholder:text-muted"
          data-testid="venue-google-query"
          autoFocus
        />
        <Button type="submit" disabled={gLoading || !gQuery.trim()} data-testid="venue-google-search-btn" className="rounded-sm bg-app hover:bg-black h-8 px-3 text-xs">
          {gLoading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Search size={13} className="mr-1.5" />} Search
        </Button>
      </form>

      {!gConfigured && (
        <div className="bg-surface border border-[#F59E0B] rounded-sm p-6" data-testid="venue-google-notconfigured">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F59E0B] mb-2">CONFIG // GOOGLE_PLACES_API_KEY</div>
          <div className="font-display text-lg font-bold text-app mb-2">Google Places isn&apos;t configured yet.</div>
          <p className="text-sm text-muted max-w-lg">
            To enable Google venue search, add a <code className="font-mono text-xs bg-surface-2 px-1 py-0.5 rounded-sm">GOOGLE_PLACES_API_KEY</code> to <code className="font-mono text-xs bg-surface-2 px-1 py-0.5 rounded-sm">backend/.env</code> and enable <em>Places API (New)</em> in Google Cloud. Once set, this tab lights up.
          </p>
        </div>
      )}

      {gConfigured && gResults === null && !gLoading && (
        <div className="bevel border-hair rounded-sm p-10 text-center">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">SYSTEM STATE / READY</div>
          <div className="font-display text-xl font-bold text-app mb-2 tracking-tight">Search live from Google Business Profiles.</div>
          <p className="text-sm text-muted max-w-md mx-auto">Type an area + venue type above (e.g. <em>&ldquo;wedding venue Portland OR&rdquo;</em>). Results show ratings, photos, hours, and reviews — save any of them to your directory in one click.</p>
        </div>
      )}

      {gConfigured && gResults && gResults.length === 0 && !gLoading && (
        <div className="bevel border-hair rounded-sm p-8 text-center text-sm text-muted">No matches. Try a broader query or a nearby city.</div>
      )}

      {gConfigured && gResults && gResults.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {gResults.map(p => (
            <GooglePlaceCard
              key={p.id}
              p={p}
              saved={gSavedIds.includes(p.id)}
              onOpen={() => onOpen(p.id)}
              onSave={() => onSave(p.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GooglePlaceCard({ p, saved, onOpen, onSave }) {
  const name = p.displayName?.text || "—";
  const photo = (p.photos || [])[0];
  const rating = p.rating ? p.rating.toFixed(1) : null;
  const count = p.userRatingCount;
  return (
    <div className="bevel border-hair card-lift rounded-sm relative" data-testid={`google-place-card-${p.id}`}>
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="aspect-[16/9] bg-surface-2 border-b border-hair overflow-hidden">
          {photo ? (
            <img alt={name} src={`${process.env.REACT_APP_BACKEND_URL}/api/venues/google/photo?name=${encodeURIComponent(photo.name)}&maxWidth=640`} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Building2 size={36} strokeWidth={1} className="text-faint" /></div>
          )}
        </div>
        <div className="p-4">
          <div className="font-mono text-[9px] tracking-widest uppercase text-muted mb-1">GOOGLE · {p.primaryTypeDisplayName?.text || (p.types || [])[0] || "PLACE"}</div>
          <div className="font-display text-base font-bold text-app tracking-tight leading-tight">{name}</div>
          {p.shortFormattedAddress && <div className="text-xs text-muted mt-1 flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} className="text-muted" /><span className="truncate">{p.shortFormattedAddress}</span></div>}
          {rating && (
            <div className="mt-2 flex items-center gap-1 text-xs">
              <Star size={11} strokeWidth={1.5} className="text-[#F59E0B]" fill="#F59E0B" />
              <span className="font-mono tabular-nums">{rating}</span>
              <span className="text-muted">· {count} reviews</span>
            </div>
          )}
        </div>
      </button>
      <div className="border-t border-hair p-2">
        {saved ? (
          <div className="w-full text-xs py-1.5 rounded-sm bg-surface-2 text-[#059669] flex items-center justify-center gap-1"><Check size={12} strokeWidth={2} /> Saved</div>
        ) : (
          <button onClick={onSave} data-testid={`google-place-save-${p.id}`} className="w-full text-xs font-medium py-1.5 rounded-sm border border-hair hover:border-[#0A0A0A] hover:bg-canvas transition-colors flex items-center justify-center gap-1.5">
            <Save size={12} strokeWidth={1.75} /> Save to directory
          </button>
        )}
      </div>
    </div>
  );
}

function GooglePlaceDetail({ placeId, onSave, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/venues/google/${placeId}`);
        setDetails(data);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not load place"); onCloseRef.current?.(); }
      finally { setLoading(false); }
    })();
  }, [placeId]);

  if (loading || !details) {
    return <div className="p-8 text-center text-muted font-mono text-xs">LOADING…</div>;
  }
  const name = details.displayName?.text;
  const summary = details.editorialSummary?.text || details.generativeSummary?.overview?.text;
  const rating = details.rating;
  const photos = details.photos || [];
  const reviews = details.reviews || [];
  const hours = details.regularOpeningHours?.weekdayDescriptions || [];
  const saved = !!details._saved_venue_id;

  return (
    <div>
      <DialogHeader className="mb-4">
        <div className="font-mono text-[10px] tracking-widest uppercase text-muted mb-1">GOOGLE PROFILE · {details.primaryTypeDisplayName?.text || "PLACE"}</div>
        <DialogTitle className="font-display text-2xl font-bold text-app tracking-tight">{name}</DialogTitle>
        <DialogDescription className="text-sm text-muted mt-1">{details.formattedAddress}</DialogDescription>
      </DialogHeader>

      {/* Save CTA */}
      <div className="mb-4">
        {saved ? (
          <div className="text-xs text-[#059669] flex items-center gap-1.5 font-medium"><Check size={13} strokeWidth={2} /> Already in your directory</div>
        ) : (
          <Button onClick={() => onSave(placeId)} data-testid="google-detail-save" className="rounded-sm bg-app hover:bg-black">
            <Save size={13} strokeWidth={2} className="mr-2" /> Save to my directory
          </Button>
        )}
      </div>

      {/* Rating + phone + website + maps link */}
      <div className="grid grid-cols-2 md:grid-cols-4 bevel border-hair mb-5 text-xs">
        <MetaCell label="RATING" value={rating ? `${rating.toFixed(1)} ★` : "—"} />
        <MetaCell label="REVIEWS" value={details.userRatingCount ? details.userRatingCount.toLocaleString() : "—"} />
        <MetaCell label="STATUS" value={(details.businessStatus || "").replace("_", " ").toLowerCase() || "—"} />
        <MetaCell label="TYPE" value={details.primaryTypeDisplayName?.text || (details.types || [])[0] || "—"} />
      </div>

      {/* About / summary */}
      {summary && (
        <Section title="About">
          <p className="text-sm text-app leading-relaxed whitespace-pre-wrap">{summary}</p>
        </Section>
      )}

      {/* Contact */}
      <Section title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {details.internationalPhoneNumber && <InfoRow icon={<Phone size={12} strokeWidth={1.5} />} label={<a href={`tel:${details.internationalPhoneNumber}`} className="text-app hover:underline">{details.internationalPhoneNumber}</a>} />}
          {details.websiteUri && <InfoRow icon={<Globe size={12} strokeWidth={1.5} />} label={<a href={details.websiteUri} target="_blank" rel="noreferrer" className="text-app hover:underline">{new URL(details.websiteUri).hostname}</a>} />}
          {details.googleMapsUri && <InfoRow icon={<ExternalLink size={12} strokeWidth={1.5} />} label={<a href={details.googleMapsUri} target="_blank" rel="noreferrer" className="text-app hover:underline">Google Maps →</a>} />}
        </div>
      </Section>

      {/* Photos */}
      {photos.length > 0 && (
        <Section title={`Photos · ${photos.length}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {photos.slice(0, 9).map((ph) => (
              <a key={ph.name} href={`${process.env.REACT_APP_BACKEND_URL}/api/venues/google/photo?name=${encodeURIComponent(ph.name)}&maxWidth=1600`} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-surface-2 overflow-hidden rounded-sm">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/venues/google/photo?name=${encodeURIComponent(ph.name)}&maxWidth=600`} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Hours */}
      {hours.length > 0 && (
        <Section title="Hours">
          <ul className="text-sm space-y-0.5 font-mono text-app">
            {hours.map((h) => <li key={h}>{h}</li>)}
          </ul>
        </Section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <Section title={`Reviews · ${reviews.length}`}>
          <div className="space-y-3">
            {reviews.slice(0, 5).map((r) => (
              <div key={r.name || `${r.authorAttribution?.displayName || "anon"}-${r.publishTime || r.relativePublishTimeDescription}`} className="border border-hair bg-inset rounded-sm p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium text-app">{r.authorAttribution?.displayName || "Anonymous"}</div>
                  <div className="flex items-center gap-1 text-xs">
                    <Star size={11} strokeWidth={1.5} className="text-[#F59E0B]" fill="#F59E0B" />
                    <span className="font-mono tabular-nums">{r.rating}</span>
                  </div>
                </div>
                {r.text?.text && <p className="text-sm text-app leading-relaxed">{r.text.text}</p>}
                {r.relativePublishTimeDescription && <div className="font-mono text-[10px] tracking-widest uppercase text-muted mt-2">{r.relativePublishTimeDescription}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// ============ VENUE DETAIL (directory) ============
function VenueDetail({ v, onEdit, onDelete, onFav, onUpload, onAddUrl, onRemoveFp, urlInput, setUrlInput }) {
  const fileInputRef = useRef(null);
  const address = [v.address_line, v.city, v.region, v.postal_code, v.country].filter(Boolean).join(", ");
  return (
    <div>
      <DialogHeader className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-widest uppercase text-muted mb-1 flex items-center gap-2">
              VEN.{v.id.slice(-6).toUpperCase()}
              {v.is_favorite && <span className="text-[#F59E0B]">★ Favorite</span>}
              {v.google_place_id && <span className="text-muted">· Google</span>}
            </div>
            <DialogTitle className="font-display text-2xl font-bold text-app tracking-tight">{v.name}</DialogTitle>
            <DialogDescription className="text-sm text-muted mt-1">
              {address ? <span className="inline-flex items-center gap-1"><MapPin size={12} strokeWidth={1.5} /> {address}</span> : "Full profile · contact, amenities, floor plans."}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onFav} className="p-2 hover:bg-surface-2" title="Favorite">
              <Star size={15} strokeWidth={1.5} fill={v.is_favorite ? "#F59E0B" : "none"} className={v.is_favorite ? "text-[#F59E0B]" : "text-muted"} />
            </button>
            <button onClick={onEdit} className="p-2 hover:bg-surface-2" data-testid="venue-detail-edit" title="Edit"><Pencil size={14} strokeWidth={1.5} /></button>
            <button onClick={onDelete} className="p-2 hover:bg-surface-2 text-muted hover:text-[#EF4444]" data-testid="venue-detail-delete" title="Delete"><Trash2 size={14} strokeWidth={1.5} /></button>
          </div>
        </div>
      </DialogHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 bevel border-hair mb-5">
        <MetaCell label="SEATED" value={v.capacity_seated ? v.capacity_seated.toLocaleString() : "—"} />
        <MetaCell label="STANDING" value={v.capacity_standing ? v.capacity_standing.toLocaleString() : "—"} />
        <MetaCell label="SIZE" value={v.square_feet ? `${v.square_feet.toLocaleString()} ft²` : "—"} />
        <MetaCell label="PRICE" value={formatPriceRange(v) || "—"} />
      </div>

      <Section title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {v.contact_name && <InfoRow icon={<Users size={12} strokeWidth={1.5} />} label={v.contact_name} />}
          {v.phone && <InfoRow icon={<Phone size={12} strokeWidth={1.5} />} label={<a href={`tel:${v.phone}`} className="text-app hover:underline">{v.phone}</a>} />}
          {v.email && <InfoRow icon={<Mail size={12} strokeWidth={1.5} />} label={<a href={`mailto:${v.email}`} className="text-app hover:underline">{v.email}</a>} />}
          {v.website && <InfoRow icon={<Globe size={12} strokeWidth={1.5} />} label={<a href={v.website} target="_blank" rel="noreferrer" className="text-app hover:underline">{v.website}</a>} />}
          {!v.contact_name && !v.phone && !v.email && !v.website && <div className="text-xs text-muted">No contact info yet.</div>}
        </div>
      </Section>

      {((v.amenities || []).length > 0 || (v.amenity_tags || []).length > 0) && (
        <Section title="Amenities">
          <div className="flex flex-wrap gap-1.5">
            {(v.amenities || []).map(a => <span key={a} className="text-xs px-2 py-1 bevel-2 border-hair rounded-sm">{a}</span>)}
            {(v.amenity_tags || []).map(t => <span key={t} className="text-xs px-2 py-1 bevel-btn-primary text-white rounded-sm">#{t}</span>)}
          </div>
        </Section>
      )}

      {(v.price_notes || v.notes) && (
        <Section title="Notes">
          {v.price_notes && <div className="text-sm text-app mb-2 whitespace-pre-wrap"><strong className="text-app">Pricing: </strong>{v.price_notes}</div>}
          {v.notes && <div className="text-sm text-app whitespace-pre-wrap">{v.notes}</div>}
        </Section>
      )}

      <Section title="Floor plans">
        {(v.floor_plans || []).length === 0 && <div className="text-xs text-muted mb-3">No floor plans yet.</div>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
          {(v.floor_plans || []).map(fp => <FloorPlanTile key={fp.id} venueId={v.id} fp={fp} onRemove={() => onRemoveFp(fp.id)} />)}
        </div>
        <div className="border border-dashed border-hair rounded-sm p-3 space-y-2">
          <input type="file" accept="image/*,application/pdf" ref={fileInputRef} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ""; } }} data-testid="venue-floorplan-upload" />
          <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="rounded-sm border-hair h-9 w-full">
            <Upload size={13} strokeWidth={1.5} className="mr-2" /> Upload image / PDF
          </Button>
          <div className="flex items-center gap-2">
            <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="…or paste a URL" className="rounded-sm h-9" data-testid="venue-floorplan-url" />
            <Button onClick={onAddUrl} variant="outline" className="rounded-sm border-hair h-9" data-testid="venue-floorplan-add-url">Add</Button>
          </div>
        </div>
      </Section>
    </div>
  );
}

function FloorPlanTile({ venueId, fp, onRemove }) {
  const isImg = fp.kind === "image";
  const isPdf = fp.kind === "pdf";
  const isUrl = fp.kind === "url";
  const downloadUrl = isUrl ? fp.url : `${process.env.REACT_APP_BACKEND_URL}/api/venues/${venueId}/floor-plans/${fp.id}/download`;
  return (
    <div className="relative border border-hair rounded-sm overflow-hidden group bg-inset" data-testid={`venue-floorplan-${fp.id}`}>
      <a href={downloadUrl} target="_blank" rel="noreferrer" className="block aspect-video">
        {isImg && <img alt={fp.name} src={downloadUrl} className="w-full h-full object-cover" />}
        {isPdf && <div className="w-full h-full flex flex-col items-center justify-center text-muted gap-1"><FileText size={22} strokeWidth={1.5} /><span className="font-mono text-[10px]">PDF</span></div>}
        {isUrl && <div className="w-full h-full flex flex-col items-center justify-center text-muted gap-1"><ExternalLink size={22} strokeWidth={1.5} /><span className="font-mono text-[10px]">LINK</span></div>}
      </a>
      <div className="p-2 border-t border-hair flex items-center gap-1">
        <span className="text-[11px] truncate flex-1 text-app" title={fp.name}>{fp.name}</span>
        <button onClick={onRemove} className="text-muted hover:text-[#EF4444]"><X size={12} strokeWidth={1.5} /></button>
      </div>
    </div>
  );
}

